import type {
	DepartmentSummary,
	IntegrationOperationalSettings,
	IntegrationSummary,
	MembershipSummary,
	WidgetSettings,
} from '@livechat/shared';
import { type FormEvent, useEffect, useId, useState } from 'react';

import {
	deleteChatAndVisitorData,
	getIntegration,
	listDepartments,
	updateDepartment,
	updateIntegration,
} from '../api.js';
import { TimezoneSelect } from './TimezoneSelect.js';
import { WidgetAppearanceEditor } from './WidgetAppearanceEditor.js';

const widgetAssetUrl =
	import.meta.env.VITE_WIDGET_URL ?? 'https://widget-chat.manorsoft.com/livechat-widget.js';

function integrationScript(publicKey: string): string {
	const separator = widgetAssetUrl.includes('?') ? '&' : '?';
	return `<script src="${widgetAssetUrl}${separator}key=${encodeURIComponent(publicKey)}" defer></script>`;
}

export type IntegrationSettingsSection = 'integration' | 'widget' | 'offline-form';

const domainPattern =
	'(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+[A-Za-z]{2,63}';

function normaliseDomains(values: string[]): string[] {
	return Array.from(new Set(values.map((domain) => domain.trim().toLowerCase()).filter(Boolean)));
}

function AllowedDomainsEditor({
	allowedDomains,
	onChange,
}: {
	allowedDomains: string[];
	onChange: (domains: string[]) => void;
}) {
	return (
		<div className="allowed-domains-editor">
			<div className="allowed-domain-list">
				{allowedDomains.map((domain, index) => (
					<div className="allowed-domain-row" key={index}>
						<input
							aria-label={`Allowed domain ${index + 1}`}
							maxLength={253}
							onChange={(event) =>
								onChange(
									allowedDomains.map((current, currentIndex) =>
										currentIndex === index
											? event.currentTarget.value
											: current,
									),
								)
							}
							pattern={domainPattern}
							placeholder="support.example.com"
							title="Enter a hostname such as support.example.com"
							value={domain}
						/>
						<button
							aria-label={`Remove ${domain || `domain ${index + 1}`}`}
							className="text-button danger"
							onClick={() =>
								onChange(
									allowedDomains.filter(
										(_, currentIndex) => currentIndex !== index,
									),
								)
							}
							type="button"
						>
							Remove
						</button>
					</div>
				))}
				{allowedDomains.length === 0 && <p>Add a domain before enabling the Chatbox.</p>}
			</div>
			<button
				className="secondary-button allowed-domain-add"
				disabled={allowedDomains.length >= 50}
				onClick={() => onChange([...allowedDomains, ''])}
				type="button"
			>
				Add domain
			</button>
		</div>
	);
}

export function IntegrationConfigurationFields({
	onOperationalSettingsChange,
	operationalSettings,
	onStatusChange,
	status,
}: {
	onOperationalSettingsChange: (settings: IntegrationOperationalSettings) => void;
	operationalSettings: IntegrationOperationalSettings;
	onStatusChange: (status: IntegrationSummary['status']) => void;
	status: IntegrationSummary['status'];
}) {
	return (
		<>
			<div className="integration-settings-fields">
				<label>
					<span>Default timezone</span>
					<TimezoneSelect
						onChange={(timezone) =>
							onOperationalSettingsChange({
								...operationalSettings,
								defaultTimezone: timezone,
							})
						}
						value={operationalSettings.defaultTimezone}
					/>
				</label>
				<label>
					<span>Date format</span>
					<select
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								dateFormat: event.currentTarget
									.value as IntegrationOperationalSettings['dateFormat'],
							})
						}
						value={operationalSettings.dateFormat}
					>
						<option value="dd/mm/yyyy">DD/MM/YYYY</option>
						<option value="mm/dd/yyyy">MM/DD/YYYY</option>
						<option value="yyyy-mm-dd">YYYY-MM-DD</option>
					</select>
				</label>
				<label>
					<span>Conversation retention (days)</span>
					<input
						max={3650}
						min={7}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								retentionDays: Number(event.currentTarget.value),
							})
						}
						type="number"
						value={operationalSettings.retentionDays}
					/>
				</label>
				<label>
					<span>Close inactive chats after (minutes)</span>
					<input
						aria-describedby="inactive-chat-help"
						max={43_200}
						min={0}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								autoCloseInactiveMinutes: Number(event.currentTarget.value),
							})
						}
						type="number"
						value={operationalSettings.autoCloseInactiveMinutes}
					/>
					<small className="field-help" id="inactive-chat-help">
						Use 0 to disable idle-chat closure. Chats close 60 seconds after the visitor
						leaves.
					</small>
				</label>
				<label>
					<span>Integration status</span>
					<select
						onChange={(event) =>
							onStatusChange(
								event.currentTarget.value as IntegrationSummary['status'],
							)
						}
						value={status}
					>
						<option value="active">Active</option>
						<option value="disabled">Disabled</option>
					</select>
				</label>
			</div>
			<div className="settings-switches integration-settings-switches">
				<label className="switch-row">
					<div className="settings-option-copy">
						<span className="settings-option-title">Use departments</span>
						<span className="settings-option-description">
							Ask visitors to choose a department and route the chat only to its
							assigned agents.
						</span>
					</div>
					<input
						checked={operationalSettings.useDepartments}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								useDepartments: event.currentTarget.checked,
							})
						}
						type="checkbox"
					/>
				</label>
				<label className="switch-row">
					<div className="settings-option-copy">
						<span className="settings-option-title">Require visitor email</span>
						<span className="settings-option-description">
							Ask for an email address before a visitor starts chatting.
						</span>
					</div>
					<input
						checked={operationalSettings.requireVisitorEmail}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								requireVisitorEmail: event.currentTarget.checked,
							})
						}
						type="checkbox"
					/>
				</label>
				<label className="switch-row">
					<div className="settings-option-copy">
						<span className="settings-option-title">Allow file uploads</span>
						<span className="settings-option-description">
							Files remain disabled until object storage and malware scanning are
							configured.
						</span>
					</div>
					<input
						checked={operationalSettings.allowFileUploads}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								allowFileUploads: event.currentTarget.checked,
							})
						}
						type="checkbox"
					/>
				</label>
				<label className="switch-row">
					<div className="settings-option-copy">
						<span className="settings-option-title">Allow whispers</span>
						<span className="settings-option-description">
							Let owners, supervisors and administrators privately coach an agent
							inside their active chat.
						</span>
					</div>
					<input
						checked={operationalSettings.allowWhispers}
						onChange={(event) =>
							onOperationalSettingsChange({
								...operationalSettings,
								allowWhispers: event.currentTarget.checked,
							})
						}
						type="checkbox"
					/>
				</label>
			</div>
		</>
	);
}

export function CobrowseSettingsFields({
	onChange,
	value,
}: {
	onChange: (settings: IntegrationOperationalSettings) => void;
	value: IntegrationOperationalSettings;
}) {
	return (
		<div className="settings-switches integration-settings-switches">
			<label className="switch-row">
				<div className="settings-option-copy">
					<span className="settings-option-title">Enable Cobrowse</span>
					<span className="settings-option-description">
						Let staff start a private, live view of an online visitor&apos;s current
						webpage. Nothing is captured until a session is started.
					</span>
				</div>
				<input
					checked={value.cobrowseEnabled}
					onChange={(event) =>
						onChange({ ...value, cobrowseEnabled: event.currentTarget.checked })
					}
					type="checkbox"
				/>
			</label>
			<label className="switch-row">
				<div className="settings-option-copy">
					<span className="settings-option-title">Mask sensitive fields</span>
					<span className="settings-option-description">
						Hide passwords, contact details, payment fields and similar sensitive values
						from the Cobrowse viewer.
					</span>
				</div>
				<input
					checked={value.cobrowseMaskSensitiveData}
					disabled={!value.cobrowseEnabled}
					onChange={(event) =>
						onChange({
							...value,
							cobrowseMaskSensitiveData: event.currentTarget.checked,
						})
					}
					type="checkbox"
				/>
			</label>
			<label className="switch-row">
				<div className="settings-option-copy">
					<span className="settings-option-title">Require visitor permission</span>
					<span className="settings-option-description">
						Ask the visitor to allow each Cobrowse session before page activity is
						shared.
					</span>
				</div>
				<input
					checked={value.cobrowseRequirePermission}
					disabled={!value.cobrowseEnabled}
					onChange={(event) =>
						onChange({
							...value,
							cobrowseRequirePermission: event.currentTarget.checked,
						})
					}
					type="checkbox"
				/>
			</label>
		</div>
	);
}

export function ChatRatingSettingsFields({
	aiChatRatingAvailable,
	onChange,
	value,
}: {
	aiChatRatingAvailable: boolean;
	onChange: (settings: IntegrationOperationalSettings) => void;
	value: IntegrationOperationalSettings;
}) {
	const options: Array<{
		description: string;
		label: string;
		mode: IntegrationOperationalSettings['chatRatingMode'];
	}> = [
		{
			description: 'Finish chats without asking the visitor or AI for a rating.',
			label: "Don't rate chats",
			mode: 'none',
		},
		{
			description: 'Ask the visitor to give the finished chat a one-to-five star rating.',
			label: 'Visitor chat rating',
			mode: 'visitor',
		},
		{
			description:
				'Use AI after the chat finishes to rate the agent’s professionalism, attitude, effort and politeness from one to five.',
			label: 'AI chat rating',
			mode: 'ai',
		},
	];
	return (
		<div className="settings-switches integration-settings-switches">
			{options.map((option) => (
				<label className="switch-row" key={option.mode}>
					<div className="settings-option-copy">
						<span className="settings-option-title">{option.label}</span>
						<span className="settings-option-description">
							{option.description}
							{option.mode === 'ai' && !aiChatRatingAvailable
								? ' Ask the Manorsoft platform owner to enable AI Chat Rating for this company.'
								: ''}
						</span>
					</div>
					<input
						checked={value.chatRatingMode === option.mode}
						disabled={option.mode === 'ai' && !aiChatRatingAvailable}
						name="chat-rating-mode"
						onChange={() => onChange({ ...value, chatRatingMode: option.mode })}
						type="radio"
						value={option.mode}
					/>
				</label>
			))}
		</div>
	);
}

export function ConversationInsightsSettingsFields({
	aiChatRatingAvailable,
	onChange,
	value,
}: {
	aiChatRatingAvailable: boolean;
	onChange: (settings: IntegrationOperationalSettings) => void;
	value: IntegrationOperationalSettings;
}) {
	return (
		<div className="settings-switches integration-settings-switches">
			<label className="switch-row">
				<div className="settings-option-copy">
					<span className="settings-option-title">Analyse completed chats</span>
					<span className="settings-option-description">
						Classify the customer’s issue, outcome and sentiment after each newly
						completed live chat for the owner-only Chat Issues Summary report.
						{!aiChatRatingAvailable
							? ' Ask the Manorsoft platform owner to enable AI Chat Rating for this company first.'
							: ''}
					</span>
				</div>
				<input
					checked={value.aiConversationInsightsEnabled}
					disabled={!aiChatRatingAvailable && !value.aiConversationInsightsEnabled}
					onChange={(event) =>
						onChange({
							...value,
							aiConversationInsightsEnabled: event.target.checked,
						})
					}
					type="checkbox"
				/>
			</label>
		</div>
	);
}
export function OfflineFormEditor({
	canManage,
	departmentOfflineModes,
	departments,
	headingId,
	onChange,
	onDepartmentOfflineModeChange,
	onOperationalSettingsChange,
	operationalSettings,
	value,
}: {
	canManage: boolean;
	departmentOfflineModes: Record<string, DepartmentSummary['routingSettings']['offlineMode']>;
	departments: DepartmentSummary[];
	headingId: string;
	onChange: (settings: WidgetSettings) => void;
	onDepartmentOfflineModeChange: (
		departmentId: string,
		mode: DepartmentSummary['routingSettings']['offlineMode'],
	) => void;
	onOperationalSettingsChange: (settings: IntegrationOperationalSettings) => void;
	operationalSettings: IntegrationOperationalSettings;
	value: WidgetSettings;
}) {
	const [departmentId, setDepartmentId] = useState('');
	const departmentForm = departmentId ? value.departmentOfflineForms[departmentId] : undefined;
	const activeForm = departmentForm ?? value.offlineForm;
	const activeEmail = departmentId
		? (operationalSettings.departmentContactFormEmails[departmentId] ??
			operationalSettings.contactFormEmail)
		: operationalSettings.contactFormEmail;

	function ensureDepartmentForm(): WidgetSettings['offlineForm'] {
		return departmentForm ?? { ...value.offlineForm };
	}

	function updateCopy(key: keyof WidgetSettings['offlineForm'], updated: string): void {
		if (departmentId) {
			onChange({
				...value,
				departmentOfflineForms: {
					...value.departmentOfflineForms,
					[departmentId]: { ...ensureDepartmentForm(), [key]: updated },
				},
			});
			return;
		}
		onChange({
			...value,
			offlineForm: { ...value.offlineForm, [key]: updated },
		});
	}

	function updateEmail(email: string): void {
		if (!departmentId) {
			onOperationalSettingsChange({
				...operationalSettings,
				contactFormEmail: email,
			});
			return;
		}
		onChange({
			...value,
			departmentOfflineForms: {
				...value.departmentOfflineForms,
				[departmentId]: ensureDepartmentForm(),
			},
		});
		onOperationalSettingsChange({
			...operationalSettings,
			departmentContactFormEmails: {
				...operationalSettings.departmentContactFormEmails,
				[departmentId]: email,
			},
		});
	}

	function useDefaultForm(): void {
		if (!departmentId) return;
		const departmentOfflineForms = { ...value.departmentOfflineForms };
		const departmentContactFormEmails = {
			...operationalSettings.departmentContactFormEmails,
		};
		delete departmentOfflineForms[departmentId];
		delete departmentContactFormEmails[departmentId];
		onChange({ ...value, departmentOfflineForms });
		onOperationalSettingsChange({
			...operationalSettings,
			departmentContactFormEmails,
		});
	}

	function customiseDepartmentForm(): void {
		if (!departmentId || departmentForm) return;
		onChange({
			...value,
			departmentOfflineForms: {
				...value.departmentOfflineForms,
				[departmentId]: { ...value.offlineForm },
			},
		});
		onOperationalSettingsChange({
			...operationalSettings,
			departmentContactFormEmails: {
				...operationalSettings.departmentContactFormEmails,
				[departmentId]: operationalSettings.contactFormEmail,
			},
		});
	}

	return (
		<>
			<section
				aria-labelledby={`${headingId}-setup`}
				className="settings-card integration-panel"
			>
				<div className="integration-panel-heading">
					<h4 className="panel-title" id={`${headingId}-setup`}>
						Offline form setup
					</h4>
					<p>Customise the form visitors use to leave a message for your team.</p>
				</div>
				<div className="offline-form-scope">
					<label>
						<span>Department</span>
						<select
							onChange={(event) => setDepartmentId(event.currentTarget.value)}
							value={departmentId}
						>
							<option value="">Default</option>
							{departments.map((department) => (
								<option key={department.id} value={department.id}>
									{department.name}
								</option>
							))}
						</select>
					</label>
					{departmentId && (
						<div className="offline-form-scope-status">
							<span>
								{departmentForm
									? 'This department has its own offline form.'
									: 'This department currently uses the default form.'}
							</span>
							{canManage && (
								<button
									className="text-button"
									onClick={
										departmentForm ? useDefaultForm : customiseDepartmentForm
									}
									type="button"
								>
									{departmentForm ? 'Use default form' : 'Customise form'}
								</button>
							)}
						</div>
					)}
				</div>
				{(!departmentId || departmentForm) && (
					<fieldset className="integration-settings-fieldset" disabled={!canManage}>
						<div className="offline-form-fields">
							<label>
								<span>Email Address</span>
								<input
									autoComplete="email"
									maxLength={320}
									onChange={(event) => updateEmail(event.currentTarget.value)}
									required
									type="email"
									value={activeEmail}
								/>
								<small className="field-help">
									Offline form messages are sent to this address.
								</small>
							</label>
							<label>
								<span>Form title</span>
								<input
									maxLength={80}
									onChange={(event) =>
										updateCopy('title', event.currentTarget.value)
									}
									required
									value={activeForm.title}
								/>
							</label>
							<label>
								<span>Form message</span>
								<textarea
									maxLength={240}
									onChange={(event) =>
										updateCopy('description', event.currentTarget.value)
									}
									required
									value={activeForm.description}
								/>
							</label>
							<label>
								<span>Submit button</span>
								<input
									maxLength={40}
									onChange={(event) =>
										updateCopy('buttonLabel', event.currentTarget.value)
									}
									required
									value={activeForm.buttonLabel}
								/>
							</label>
							<label>
								<span>Confirmation message</span>
								<textarea
									maxLength={240}
									onChange={(event) =>
										updateCopy('confirmationMessage', event.currentTarget.value)
									}
									required
									value={activeForm.confirmationMessage}
								/>
							</label>
						</div>
					</fieldset>
				)}
			</section>
			{departmentId && (
				<section
					aria-labelledby={`${headingId}-department-unavailable`}
					className="settings-card integration-panel"
				>
					<div className="integration-panel-heading">
						<h4 className="panel-title" id={`${headingId}-department-unavailable`}>
							When the team is unreachable
						</h4>
						<p>Choose what visitors see when this department cannot accept a chat.</p>
					</div>
					<fieldset className="integration-settings-fieldset" disabled={!canManage}>
						<div className="offline-form-fields">
							<label>
								<span>Unavailable behaviour</span>
								<select
									onChange={(event) =>
										onDepartmentOfflineModeChange(
											departmentId,
											event.currentTarget
												.value as DepartmentSummary['routingSettings']['offlineMode'],
										)
									}
									value={departmentOfflineModes[departmentId] ?? 'form'}
								>
									<option value="hidden">Hide Department</option>
									<option value="form">Show Offline Form</option>
								</select>
							</label>
						</div>
					</fieldset>
				</section>
			)}
		</>
	);
}

export function TenantIntegration({
	membership,
	onDateFormatChanged,
	section,
}: {
	membership: MembershipSummary;
	onDateFormatChanged?: (format: IntegrationOperationalSettings['dateFormat']) => void;
	section: IntegrationSettingsSection;
}) {
	const headingId = useId();
	const [integration, setIntegration] = useState<IntegrationSummary>();
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [draftDepartmentOfflineModes, setDraftDepartmentOfflineModes] = useState<
		Record<string, DepartmentSummary['routingSettings']['offlineMode']>
	>({});
	const [draftDomains, setDraftDomains] = useState<string[]>([]);
	const [draftOperationalSettings, setDraftOperationalSettings] =
		useState<IntegrationOperationalSettings>();
	const [draftStatus, setDraftStatus] = useState<IntegrationSummary['status']>('active');
	const [draftWidgetSettings, setDraftWidgetSettings] = useState<WidgetSettings>();
	const [busy, setBusy] = useState(false);
	const [deletingData, setDeletingData] = useState(false);
	const [confirmDataDeletion, setConfirmDataDeletion] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	const canManage = membership.role === 'owner' || membership.role === 'admin';

	useEffect(() => {
		setError(undefined);
		setIntegration(undefined);
		void Promise.all([
			getIntegration(membership.tenantId),
			listDepartments(membership.tenantId),
		])
			.then(([loaded, loadedDepartments]) => {
				setIntegration(loaded);
				setDepartments(loadedDepartments);
				setDraftDepartmentOfflineModes(
					Object.fromEntries(
						loadedDepartments.map((department) => [
							department.id,
							department.routingSettings.offlineMode,
						]),
					),
				);
				setDraftDomains([...loaded.allowedDomains]);
				setDraftOperationalSettings(loaded.operationalSettings);
				setDraftStatus(loaded.status);
				setDraftWidgetSettings(loaded.widgetSettings);
			})
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error
						? caught.message
						: 'Could not load integration settings',
				),
			);
	}, [membership.tenantId]);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!draftOperationalSettings || !draftWidgetSettings) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const changedDepartments = departments.filter(
				(department) =>
					draftDepartmentOfflineModes[department.id] !== undefined &&
					draftDepartmentOfflineModes[department.id] !==
						department.routingSettings.offlineMode,
			);
			const [updated, updatedDepartments] = await Promise.all([
				updateIntegration(membership.tenantId, {
					allowedDomains: normaliseDomains(draftDomains),
					operationalSettings: draftOperationalSettings,
					status: draftStatus,
					widgetSettings: {
						...draftWidgetSettings,
						enableVisitorChatRating:
							draftOperationalSettings.chatRatingMode === 'visitor',
					},
				}),
				Promise.all(
					changedDepartments.map((department) =>
						updateDepartment(membership.tenantId, department.id, {
							routingSettings: {
								...department.routingSettings,
								offlineMode: draftDepartmentOfflineModes[department.id]!,
							},
						}),
					),
				),
			]);
			setIntegration(updated);
			if (updatedDepartments.length) {
				const byId = new Map(
					updatedDepartments.map((department) => [department.id, department]),
				);
				setDepartments((current) =>
					current.map((department) => byId.get(department.id) ?? department),
				);
			}
			setDraftDomains([...updated.allowedDomains]);
			setDraftOperationalSettings(updated.operationalSettings);
			onDateFormatChanged?.(updated.operationalSettings.dateFormat);
			setDraftStatus(updated.status);
			setDraftWidgetSettings(updated.widgetSettings);
			setMessage('Settings saved.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save integration settings',
			);
		} finally {
			setBusy(false);
		}
	}

	async function deleteData(): Promise<void> {
		setDeletingData(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const deleted = await deleteChatAndVisitorData(membership.tenantId);
			setConfirmDataDeletion(false);
			setMessage(
				`Deleted ${deleted.conversationsDeleted} conversations and ${deleted.visitorsDeleted} visitors.`,
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not delete chat data');
		} finally {
			setDeletingData(false);
		}
	}

	if (!integration || !draftOperationalSettings || !draftWidgetSettings) {
		return (
			<div className="settings-page">
				{error ? <div className="alert error">{error}</div> : <p>Loading settings…</p>}
			</div>
		);
	}

	const snippet = integrationScript(integration.publicKey);
	const copy = {
		'offline-form': {
			description: 'Choose what visitors see when nobody can accept another chat.',
			title: 'Offline Form',
		},
		integration: {
			description:
				'Authorised domains, visitor requirements and the code used to connect Livechat.',
			title: 'Integration',
		},
		widget: {
			description: 'Control how the Chatbox launcher and conversation panel look.',
			title: 'Chatbox',
		},
	}[section];
	const saveControl = canManage ? (
		<button
			className="primary-button integration-save"
			disabled={busy || !normaliseDomains(draftDomains).length}
		>
			{busy ? 'Saving…' : `Save ${copy.title.toLowerCase()}`}
		</button>
	) : (
		<p className="settings-readonly-note">
			Only owners and administrators can change these settings.
		</p>
	);

	return (
		<div className="settings-page integration-settings-page">
			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}
			<div className="settings-section-heading">
				<h4 id={headingId}>{copy.title}</h4>
				<p>{copy.description}</p>
			</div>
			{section === 'integration' ? (
				<>
					<form
						className="integration-settings-form"
						onSubmit={(event) => void save(event)}
					>
						<section
							aria-labelledby={`${headingId}-code`}
							className="settings-card integration-panel integration-code-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-code`}>
									Integration code
								</h4>
								<p>
									Place this before the closing body tag on every allowed domain.
								</p>
							</div>
							<div className="code-block">
								<code>{snippet}</code>
								<button
									onClick={() => void navigator.clipboard.writeText(snippet)}
									type="button"
								>
									Copy
								</button>
							</div>
						</section>
						<section
							aria-labelledby={`${headingId}-chat-rating`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-chat-rating`}>
									Chat rating
								</h4>
								<p>Choose one way to rate completed chats.</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<ChatRatingSettingsFields
									aiChatRatingAvailable={integration.aiChatRatingAvailable}
									onChange={setDraftOperationalSettings}
									value={draftOperationalSettings}
								/>
							</fieldset>
						</section>
						<section
							aria-labelledby={`${headingId}-conversation-insights`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4
									className="panel-title"
									id={`${headingId}-conversation-insights`}
								>
									AI conversation insights
								</h4>
								<p>
									Build a private daily view of the reasons customers contact you.
								</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<ConversationInsightsSettingsFields
									aiChatRatingAvailable={integration.aiChatRatingAvailable}
									onChange={setDraftOperationalSettings}
									value={draftOperationalSettings}
								/>
							</fieldset>
						</section>{' '}
						<section
							aria-labelledby={`${headingId}-configuration`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-configuration`}>
									Configuration
								</h4>
								<p>Manage routing, visitor requirements and data retention.</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<IntegrationConfigurationFields
									onOperationalSettingsChange={setDraftOperationalSettings}
									operationalSettings={draftOperationalSettings}
									onStatusChange={setDraftStatus}
									status={draftStatus}
								/>
							</fieldset>
						</section>
						<section
							aria-labelledby={`${headingId}-cobrowse`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-cobrowse`}>
									Cobrowse
								</h4>
								<p>Control live, view-only assistance on visitor webpages.</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<CobrowseSettingsFields
									onChange={setDraftOperationalSettings}
									value={draftOperationalSettings}
								/>
							</fieldset>
						</section>
						<section
							aria-labelledby={`${headingId}-domains`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-domains`}>
									Allowed domains
								</h4>
								<p>Only pages on these hostnames can load this integration.</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<AllowedDomainsEditor
									allowedDomains={draftDomains}
									onChange={setDraftDomains}
								/>
							</fieldset>
						</section>
						<section
							aria-labelledby={`${headingId}-unavailable`}
							className="settings-card integration-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id={`${headingId}-unavailable`}>
									When nobody is available
								</h4>
								<p>Choose what happens when nobody can accept another chat.</p>
							</div>
							<fieldset
								className="integration-settings-fieldset"
								disabled={!canManage}
							>
								<div className="offline-form-fields">
									<label>
										<span>Unavailable behaviour</span>
										<select
											onChange={(event) =>
												setDraftWidgetSettings({
													...draftWidgetSettings,
													availabilityMode: event.currentTarget
														.value as WidgetSettings['availabilityMode'],
												})
											}
											value={draftWidgetSettings.availabilityMode}
										>
											<option value="hidden">Hide Chatbox</option>
											<option value="contact_form">Show Offline Form</option>
										</select>
									</label>
								</div>
							</fieldset>
						</section>
						{saveControl}
					</form>
					{membership.role === 'owner' && (
						<section className="settings-card danger-zone integration-data-danger-zone">
							<div>
								<strong>Delete chat and visitor logs</strong>
								<p>
									Permanently delete every conversation, message, visitor session
									and blocked-visitor record for this company. Integration
									settings and team members are kept.
								</p>
							</div>
							<button
								className="danger-button"
								disabled={deletingData}
								onClick={() => setConfirmDataDeletion(true)}
								type="button"
							>
								Delete all logs
							</button>
						</section>
					)}
				</>
			) : section === 'widget' ? (
				<form className="chat-box-settings-form" onSubmit={(event) => void save(event)}>
					<fieldset className="integration-settings-fieldset" disabled={!canManage}>
						<WidgetAppearanceEditor
							brandingHtml={integration.brandingHtml}
							brandingRemovalAllowed={integration.brandingRemovalAllowed}
							onChange={setDraftWidgetSettings}
							value={draftWidgetSettings}
						/>
					</fieldset>
					{saveControl}
				</form>
			) : (
				<form className="integration-settings-form" onSubmit={(event) => void save(event)}>
					<OfflineFormEditor
						canManage={canManage}
						departmentOfflineModes={draftDepartmentOfflineModes}
						departments={departments}
						headingId={headingId}
						onChange={setDraftWidgetSettings}
						onDepartmentOfflineModeChange={(departmentId, mode) =>
							setDraftDepartmentOfflineModes((current) => ({
								...current,
								[departmentId]: mode,
							}))
						}
						onOperationalSettingsChange={setDraftOperationalSettings}
						operationalSettings={draftOperationalSettings}
						value={draftWidgetSettings}
					/>
					{saveControl}
				</form>
			)}
			{confirmDataDeletion && (
				<div className="dialog-backdrop" role="presentation">
					<section aria-modal="true" className="dialog-card" role="dialog">
						<h3>Delete all chat and visitor logs?</h3>
						<p>
							This cannot be undone. All current chats will also be removed and
							connected visitors will start a new session.
						</p>
						<div className="dialog-actions">
							<button
								className="secondary-button"
								disabled={deletingData}
								onClick={() => setConfirmDataDeletion(false)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="danger-button"
								disabled={deletingData}
								onClick={() => void deleteData()}
								type="button"
							>
								{deletingData ? 'Deleting…' : 'Delete all logs'}
							</button>
						</div>
					</section>
				</div>
			)}
		</div>
	);
}
